// fmt — the few strings the Field screens print that the design system's
// helpers do not already cover. Times go through utils/timezone (the user's
// zone, UTC-anchored parse); date-only strings are parsed at local noon
// through utils/scheduleDates so a 'YYYY-MM-DD' never shifts a day.

import { formatDateInUserTZ } from '../../utils/timezone';
import { atNoon, addDaysISO } from '../../utils/scheduleDates';
import { formatClock } from '../../ui/fw';

/** '2026-09-12T10:15:00.000Z' → '6:15 AM' in the user's zone. */
export function timeOf(iso) {
  if (!iso) return '';
  return formatDateInUserTZ(iso, { hour: 'numeric', minute: '2-digit', hour12: true });
}

/** '2026-09-08' → 'Mon, Sep 8'. */
export function dayLabel(isoDate) {
  if (!isoDate) return '';
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(atNoon(isoDate));
}

/** '2026-09-08' → 'Sep 8'. */
export function shortDate(isoDate) {
  if (!isoDate) return '';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(atNoon(isoDate));
}

/** ('2026-09-08', '2026-09-14') → 'Sep 8 – Sep 14'. */
export function rangeLabel(start, end) {
  if (!start) return '';
  return end ? `${shortDate(start)} – ${shortDate(end)}` : shortDate(start);
}

/** Seconds → '2:05' (H:MM) for a finished entry. */
export function durationLabel(seconds) {
  return formatClock(seconds || 0);
}

/** 'as of 7:42 AM' for a cached bundle. */
export function staleLabel(at) {
  return at ? `as of ${timeOf(at)}` : '';
}

export function weekBefore(startDate) { return addDaysISO(startDate, -7); }
export function weekAfter(startDate) { return addDaysISO(startDate, 7); }

/** 'Sat Sep 12, 8:03 AM' for a note or photo timestamp. */
export function whenLabel(iso) {
  if (!iso) return '';
  return formatDateInUserTZ(iso, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

export function plural(n, one, many) {
  return `${n} ${n === 1 ? one : (many || `${one}s`)}`;
}

/** One-letter weekday heads, indexed by `Date#getDay()` (0 = Sunday). */
const DAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/**
 * The seven days of the `week` block on `GET /api/field/today`, as cells.
 *
 * docs/PARITY_2026-09-08.md §22: the server has always sent
 * `week: { start_date, end_date, hours_by_day, total_hours, timesheet }` and
 * Today.js read none of it, so a crew member could not see the shape of their
 * own week without opening My hours and stepping through each day.
 *
 * Built from `start_date` plus 0..6 rather than by iterating `hours_by_day`,
 * because **the payload only carries days that have clock lines** — measured
 * on the wire, `hours_by_day` was `{ "2026-09-09": 0 }` for a week with one
 * entry. Iterating the object would render a one-cell "week". Seven cells
 * always, and a day nobody worked is 0.
 *
 * `worked` is `hours > 0` and NOT "the server mentioned this date", which is a
 * distinction this function briefly tried to draw and should not. The server
 * can report a day AS zero — measured: a five-second clock entry produced
 * `{ "2026-09-09": 0 }` — and tinting that cell as a worked day while the
 * number in it reads 0 is a screen arguing with itself. A day is worked when
 * it has hours on it. (Written down because the first version carried a
 * `reported &&` conjunct that could never change the answer: an absent key and
 * a key holding 0 both give 0 hours, so the extra term was dead. It was caught
 * by sabotaging it and watching nothing go red — CLAUDE.md rule 8.)
 *
 * Dates go through `atNoon` — see this file's header. Parsed bare,
 * '2026-09-07' is midnight UTC and every label west of Greenwich shifts a day.
 *
 * Returns [] when there is no week block at all, which happens when the phone
 * renders an older cached bundle offline; a throw there blanks the first
 * screen of the app.
 */
export function weekDays(week) {
  const start = week && week.start_date;
  if (!start) return [];
  const byDay = (week && week.hours_by_day) || {};
  const days = [];
  for (let i = 0; i < 7; i += 1) {
    const date = addDaysISO(start, i);
    // `Number(undefined)` is NaN and `Number(null)` is 0, so an absent day and
    // a null one both land on 0 without needing a hasOwnProperty check.
    const raw = Number(byDay[date]);
    const hours = Number.isFinite(raw) ? raw : 0;
    days.push({
      date,
      label: DAY_LETTERS[atNoon(date).getDay()],
      hours,
      worked: hours > 0,
    });
  }
  return days;
}

/**
 * Hours for a day cell — as few characters as stay truthful.
 *
 * `ui/fw formatHours` is `toFixed(2)`, so 8 becomes '8.00': five characters,
 * and seven of those side by side do not fit across a 320px phone.
 * docs/APP_REBUILD.md §2 says content decides the layout, so the NUMBER gets
 * shorter rather than the strip getting a scrollbar. One decimal, and none at
 * all for a whole number.
 *
 * A non-number reads as '0' rather than 'NaN'. The total elsewhere still uses
 * formatHours, where there is room for it.
 */
export function compactHours(h) {
  const n = Number(h);
  if (!Number.isFinite(n)) return '0';
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/**
 * When this job is meant to happen, as one string — or null when nothing was
 * scheduled.
 *
 * `scheduled_date` and `scheduled_end_date` have ridden in on every job the
 * Field endpoints return (server/routes/field.js shapeJob) since the rebuild,
 * and the job SCREEN drew neither: a crew member could open the job they were
 * standing in front of and not find out whether it was today's work or
 * Thursday's. A one-day job reads as its weekday, a multi-day one as a range.
 *
 * Dates go through dayLabel/rangeLabel, so they are parsed at local noon and a
 * 'YYYY-MM-DD' never shifts a day west of Greenwich — see this file's header.
 */
export function scheduleLabel(job) {
  const start = job && job.scheduled_date;
  if (!start) return null;
  const end = job && job.scheduled_end_date;
  return end && end !== start ? rangeLabel(start, end) : dayLabel(start);
}

/**
 * Seconds of FINISHED clock time in a job's `entries` list.
 *
 * DISPLAY ONLY, and deliberately not pay. It sums the `duration` the server
 * already computed for each closed entry so the job screen can put the
 * estimate next to what has actually been spent; nothing here rounds, prices
 * or reaches a payroll figure — those come from server/utils/paystubCalc.js
 * behind the approval workflow (CLAUDE.md rule 4).
 *
 * An entry with no `end_time` is still running and contributes nothing: its
 * `duration` is null until the clock stops, and counting a running entry as
 * zero is the same answer with fewer branches.
 */
export function loggedSeconds(entries) {
  if (!Array.isArray(entries)) return 0;
  return entries.reduce((n, e) => {
    if (!e || !e.end_time) return n;
    const d = Number(e.duration);
    return Number.isFinite(d) && d > 0 ? n + d : n;
  }, 0);
}
